import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bookmark, Plus, Search, Trash2, Car, AlertCircle, CheckCircle2 } from 'lucide-react';
import { api } from '../lib/api';
import { formatBRL, formatPercent } from '../lib/format';
import { calcProportionalTradeAllocations } from '../lib/tradeCalc';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Separator } from './ui/separator';
import CurrencyInput from './CurrencyInput';

/**
 * TradeVehicleDialog
 * Captures minimum data for a vehicle received in trade:
 *  - plate (with check-plate prefill), brand, model, version (required)
 *  - valuation (= payment amount)
 *  - composition: mirror seller's or customize
 */
export default function TradeVehicleDialog({
  open,
  onOpenChange,
  sellerVehicle, // the vehicle being sold (allocations + purchase_price)
  salePrice, // current sale price (from sell dialog)
  totalExpenses, // total expenses of seller vehicle
  partners, // list of partners in tenant
  initial, // existing payment to edit
  onConfirm, // (payment) => void
}) {
  const [form, setForm] = useState({
    plate: '',
    brand: '',
    model: '',
    version: '',
    amount: '',
  });
  const [mirror, setMirror] = useState(true);
  const [allocations, setAllocations] = useState([]);
  const [plateStatus, setPlateStatus] = useState(null); // {checking, exists, in_use, vehicle, message}
  const [submitting, setSubmitting] = useState(false);
  // Link existing stock vehicle as trade
  const [showLinkPicker, setShowLinkPicker] = useState(false);
  const [linkableVehicles, setLinkableVehicles] = useState([]);
  const [loadingLinkable, setLoadingLinkable] = useState(false);
  const [linkedVehicleId, setLinkedVehicleId] = useState(null); // when set, save uses link mode

  useEffect(() => {
    if (open) {
      if (initial?.trade_vehicle) {
        const tv = initial.trade_vehicle;
        setForm({
          plate: tv.plate || '',
          brand: tv.brand || '',
          model: tv.model || '',
          version: tv.version || '',
          amount: initial.amount ?? '',
        });
        const initAllocs = tv.allocations || [];
        if (initAllocs.length > 0) {
          setMirror(false);
          setAllocations(initAllocs);
        } else {
          setMirror(true);
          setAllocations([]);
        }
      } else {
        setForm({ plate: '', brand: '', model: '', version: '', amount: '' });
        setMirror(true);
        setAllocations([]);
      }
      setPlateStatus(null);
      setLinkedVehicleId(null);
      setShowLinkPicker(false);
    }
  }, [open, initial]);

  const openLinkPicker = async () => {
    setShowLinkPicker(true);
    setLoadingLinkable(true);
    try {
      const params = sellerVehicle?.id ? `?exclude_id=${sellerVehicle.id}` : '';
      const { data } = await api.get(`/vehicles/active-for-trade${params}`);
      setLinkableVehicles(data || []);
    } catch (err) {
      toast.error('Erro ao carregar veículos do estoque');
    } finally {
      setLoadingLinkable(false);
    }
  };

  const selectLinkVehicle = (v) => {
    setLinkedVehicleId(v.id);
    setForm({
      plate: v.plate || '',
      brand: v.brand || '',
      model: v.model || '',
      version: v.version || '',
      amount: form.amount, // mantém valor já digitado
    });
    setPlateStatus({ exists: true, in_use: false, linked: true, vehicle: v, message: 'Vinculado ao estoque' });
    setShowLinkPicker(false);
    toast.success(`Vinculado: ${v.brand} ${v.model} ${v.plate}`);
  };

  const unlinkVehicle = () => {
    setLinkedVehicleId(null);
    setPlateStatus(null);
    toast.info('Vínculo removido');
  };

  const checkPlate = async () => {
    const plate = form.plate.trim().toUpperCase();
    if (!plate) {
      setPlateStatus(null);
      return;
    }
    setPlateStatus({ checking: true });
    try {
      const { data } = await api.get(`/vehicles/check-plate/${plate}`);
      if (data.exists) {
        setPlateStatus({ ...data, checking: false });
        if (data.exists && !data.in_use && data.vehicle) {
          // Prefill
          setForm((f) => ({
            ...f,
            brand: f.brand || data.vehicle.brand || '',
            model: f.model || data.vehicle.model || '',
            version: f.version || data.vehicle.version || '',
          }));
        }
      } else {
        setPlateStatus({ checking: false, exists: false });
      }
    } catch (err) {
      setPlateStatus(null);
    }
  };

  const sellerAllocations = (sellerVehicle?.allocations || []).map((a) => ({
    partner_id: a.partner_id,
    contribution_pct: a.contribution_pct,
    profit_pct: a.profit_pct,
  }));

  // Calculate proportional allocations (preview when "Espelhar" is selected)
  const proportionalAllocations = React.useMemo(() => {
    if (!sellerAllocations.length || !salePrice || salePrice <= 0) return [];
    return calcProportionalTradeAllocations({
      sellerAllocations,
      purchasePrice: sellerVehicle?.purchase_price || 0,
      salePrice,
      totalExpenses: totalExpenses || 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerVehicle?.id, salePrice, totalExpenses, JSON.stringify(sellerAllocations)]);

  const addAllocation = () => {
    const remaining = partners.filter((p) => !allocations.find((a) => a.partner_id === p.id));
    if (remaining.length === 0) {
      toast.warning('Nenhum sócio disponível');
      return;
    }
    setAllocations((a) => [...a, { partner_id: remaining[0].id, contribution_pct: 0, profit_pct: 0 }]);
  };
  const updateAlloc = (idx, field, value) => {
    setAllocations((a) => a.map((al, i) => (i === idx ? { ...al, [field]: value } : al)));
  };
  const removeAlloc = (idx) => setAllocations((a) => a.filter((_, i) => i !== idx));

  const startCustomize = () => {
    setMirror(false);
    if (allocations.length === 0) {
      setAllocations(sellerAllocations);
    }
  };

  const sumContrib = allocations.reduce((s, a) => s + Number(a.contribution_pct || 0), 0);
  const sumProfit = allocations.reduce((s, a) => s + Number(a.profit_pct || 0), 0);
  const contribValid = mirror || Math.abs(sumContrib - 100) < 0.01;
  const profitValid = mirror || Math.abs(sumProfit - 100) < 0.01;

  const partnerName = (id) => partners.find((p) => p.id === id)?.name || id;

  const handleConfirm = async () => {
    if (!form.plate.trim() || !form.brand.trim() || !form.model.trim() || !form.version.trim()) {
      toast.error('Placa, marca, modelo e versão são obrigatórios');
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error('Valor avaliado é obrigatório');
      return;
    }
    if (plateStatus?.in_use && !linkedVehicleId) {
      toast.error('Esta placa já está em uso em outro veículo ativo');
      return;
    }
    if (!mirror && (!contribValid || !profitValid)) {
      toast.error('Composição customizada: percentuais devem somar 100%');
      return;
    }
    setSubmitting(true);
    onConfirm({
      method: 'TRADE',
      amount: Number(form.amount),
      trade_vehicle: {
        plate: form.plate.trim().toUpperCase(),
        brand: form.brand.trim(),
        model: form.model.trim(),
        version: form.version.trim(),
        link_existing_vehicle_id: linkedVehicleId || null,
        allocations: mirror ? null : allocations.map((a) => ({
          partner_id: a.partner_id,
          contribution_pct: Number(a.contribution_pct),
          profit_pct: Number(a.profit_pct),
        })),
      },
    });
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Car className="h-5 w-5" /> Veículo recebido em troca</DialogTitle>
          <p className="text-xs text-muted-foreground">Cadastro rápido com dados mínimos. Os demais campos (chassi, renavam, KM, etc.) podem ser completados depois.</p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Link existing vehicle banner */}
          {linkedVehicleId ? (
            <div className="rounded-lg border-2 border-blue-300 bg-blue-50 p-3 flex items-start gap-2" data-testid="trade-linked-banner">
              <Car className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-blue-900">Vinculado a veículo do estoque</div>
                <div className="text-xs text-blue-800 mt-0.5">
                  Os dados do veículo serão atualizados (valor, composição, status) ao salvar. O histórico de despesas permanece.
                </div>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={unlinkVehicle} data-testid="trade-unlink">
                Desvincular
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openLinkPicker}
              className="w-full border-dashed bg-blue-50/50 hover:bg-blue-50 border-blue-300 text-blue-800"
              data-testid="trade-open-link-picker"
            >
              <Car className="h-4 w-4 mr-1.5" /> Vincular veículo já existente no estoque
            </Button>
          )}

          {/* Vehicle picker list */}
          {showLinkPicker && (
            <div className="rounded-lg border border-blue-200 bg-card p-3 space-y-2" data-testid="trade-link-picker">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold">Selecionar veículo do estoque</h4>
                <Button type="button" variant="ghost" size="sm" onClick={() => setShowLinkPicker(false)}>×</Button>
              </div>
              {loadingLinkable ? (
                <div className="text-sm text-muted-foreground py-3 text-center">Carregando...</div>
              ) : linkableVehicles.length === 0 ? (
                <div className="text-sm text-muted-foreground py-3 text-center">Nenhum veículo disponível no estoque.</div>
              ) : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {linkableVehicles.map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => selectLinkVehicle(v)}
                      className="w-full text-left p-2.5 rounded-lg border border-border/70 hover:bg-muted/40 hover:border-blue-300 transition-colors flex items-center justify-between gap-2"
                      data-testid={`trade-link-vehicle-${v.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Car className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">
                            {v.brand} {v.model}{v.version ? ' ' + v.version : ''} {v.year ? `(${v.year})` : ''}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Placa {v.plate || '—'} · {v.status} · Compra: {formatBRL(v.purchase_price || 0)}
                          </div>
                        </div>
                      </div>
                      <CheckCircle2 className="h-4 w-4 text-blue-600 shrink-0 opacity-0 hover:opacity-100" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <Label>Placa *</Label>
              <Input
                value={form.plate}
                onChange={(e) => { setForm((f) => ({ ...f, plate: e.target.value.toUpperCase() })); setPlateStatus(null); }}
                onBlur={linkedVehicleId ? undefined : checkPlate}
                placeholder="ABC1D23"
                disabled={!!linkedVehicleId}
                data-testid="trade-plate-input"
              />
            </div>
            <Button type="button" variant="outline" onClick={checkPlate} disabled={!form.plate || !!linkedVehicleId} data-testid="trade-check-plate">
              <Search className="h-4 w-4 mr-1.5" /> Verificar
            </Button>
          </div>

          {plateStatus && !plateStatus.checking && (
            <div className={`rounded-lg p-3 text-sm flex items-start gap-2 ${
              plateStatus.in_use ? 'bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))] border border-red-200' :
              plateStatus.exists ? 'bg-blue-50 text-blue-800 border border-blue-200' :
              'bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] border border-emerald-200'
            }`}>
              {plateStatus.in_use ? <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> :
                plateStatus.exists ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> :
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />}
              <div>
                <div className="font-medium">
                  {plateStatus.in_use ? 'Placa já em uso' :
                   plateStatus.exists ? 'Veículo já passou pelo estoque' :
                   'Placa disponível'}
                </div>
                <div className="text-xs mt-0.5">
                  {plateStatus.in_use && plateStatus.message}
                  {!plateStatus.in_use && plateStatus.exists && 'Os dados (marca, modelo, versão) foram pré-preenchidos com base no histórico.'}
                  {!plateStatus.exists && 'Este veículo será cadastrado no estoque.'}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Marca *</Label>
              <Input value={form.brand} onChange={(e) => setForm((f) => ({ ...f, brand: e.target.value }))} data-testid="trade-brand-input" />
            </div>
            <div>
              <Label>Modelo *</Label>
              <Input value={form.model} onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))} data-testid="trade-model-input" />
            </div>
            <div>
              <Label>Versão *</Label>
              <Input value={form.version} onChange={(e) => setForm((f) => ({ ...f, version: e.target.value }))} placeholder="Ex: EXL, GTI" data-testid="trade-version-input" />
            </div>
          </div>

          <div>
            <Label>Valor avaliado *</Label>
            <CurrencyInput value={form.amount} onChange={(v) => setForm((f) => ({ ...f, amount: v }))} data-testid="trade-amount-input" />
            <p className="text-[11px] text-muted-foreground mt-1">Quanto o veículo recebido vale na negociação (= valor desta forma de pagamento).</p>
          </div>

          <Separator />

          <div>
            <Label>Composição societária do veículo recebido</Label>
            <div className="mt-2 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="trade-mirror-radio">
                <input
                  type="radio"
                  checked={mirror}
                  onChange={() => setMirror(true)}
                  className="mt-0.5 accent-[hsl(var(--primary))]"
                />
                <div className="flex-1">
                  <div className="font-medium">Calcular proporcional à distribuição prevista</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    O aporte de cada sócio no veículo recebido reflete sua parte no que tem direito a receber nesta venda. O resultado mantém a proporção original (fator) e é normalizado para 100%.
                  </div>
                  {mirror && proportionalAllocations.length > 0 && (
                    <div className="mt-2 rounded-lg bg-blue-50 border border-blue-200 p-2.5 space-y-1">
                      <div className="text-[11px] font-medium uppercase tracking-wide text-blue-900 mb-1">Composição calculada</div>
                      {proportionalAllocations.map((a) => (
                        <div key={a.partner_id} className="flex items-center justify-between text-xs">
                          <span className="text-blue-900 truncate">{partnerName(a.partner_id)}</span>
                          <span className="tabular text-blue-900">
                            <span className="font-semibold">{formatPercent(a.contribution_pct)}</span>
                            <span className="mx-1.5 text-blue-400">aporte</span>
                            <span className="mx-1.5 text-blue-300">·</span>
                            <span className="font-semibold">{formatPercent(a.profit_pct)}</span>
                            <span className="ml-1 text-blue-400">resultado</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {mirror && proportionalAllocations.length === 0 && (
                    <div className="mt-2 text-[11px] text-muted-foreground italic">
                      Informe o valor total da venda no diálogo principal para visualizar a composição calculada.
                    </div>
                  )}
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="trade-custom-radio">
                <input
                  type="radio"
                  checked={!mirror}
                  onChange={startCustomize}
                  className="mt-0.5 accent-[hsl(var(--primary))]"
                />
                <div className="flex-1">
                  <div className="font-medium">Personalizar</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Definir manualmente o % de aporte e resultado de cada sócio.</div>
                </div>
              </label>
            </div>

            {!mirror && (
              <div className="mt-3 space-y-3 border border-border/70 rounded-lg p-3">
                {allocations.map((alloc, idx) => {
                  const available = partners.filter(
                    (p) => p.id === alloc.partner_id || !allocations.find((a) => a.partner_id === p.id),
                  );
                  return (
                    <div key={idx} className="grid grid-cols-[1fr_100px_100px_40px] gap-2 items-end">
                      <div>
                        <Label className="text-xs">Sócio</Label>
                        <Select value={alloc.partner_id} onValueChange={(v) => updateAlloc(idx, 'partner_id', v)}>
                          <SelectTrigger className="truncate"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {available.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">% Aporte</Label>
                        <Input type="number" step="0.01" min="0" max="100" className="text-right tabular"
                          value={alloc.contribution_pct}
                          onChange={(e) => updateAlloc(idx, 'contribution_pct', e.target.value === '' ? 0 : Number(e.target.value))}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">% Resultado</Label>
                        <Input type="number" step="0.01" min="0" max="100" className="text-right tabular"
                          value={alloc.profit_pct}
                          onChange={(e) => updateAlloc(idx, 'profit_pct', e.target.value === '' ? 0 : Number(e.target.value))}
                        />
                      </div>
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeAlloc(idx)}>
                        <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                    </div>
                  );
                })}
                <Button type="button" size="sm" variant="outline" onClick={addAllocation}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar sócio
                </Button>
                {allocations.length > 0 && (
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className={`rounded-md p-2 ${contribValid ? 'bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]' : 'bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]'}`}>
                      Soma % Aporte: <span className="font-semibold tabular">{formatPercent(sumContrib)}</span>
                    </div>
                    <div className={`rounded-md p-2 ${profitValid ? 'bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]' : 'bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]'}`}>
                      Soma % Resultado: <span className="font-semibold tabular">{formatPercent(sumProfit)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || plateStatus?.in_use}
            className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90"
            data-testid="trade-confirm-button"
          >
            {initial ? 'Atualizar troca' : 'Adicionar troca'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
